'use client';

/**
 * Protected Route Component
 * Requires authentication to access the wrapped content
 */

import React, { useEffect } from 'react';
import { useAuth } from './AuthContext';
import { usePathname } from 'next/navigation';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireVerifiedEmail?: boolean;
}

export function ProtectedRoute({
  children,
  fallback,
  requireVerifiedEmail = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, login } = useAuth();
  const pathname = usePathname();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Redirect to login with return URL
      login({ returnTo: pathname });
    }
  }, [isAuthenticated, isLoading, login, pathname]);

  // Show loading state
  if (isLoading) {
    return fallback || <LoadingState />;
  }

  // Not authenticated - show fallback or loading
  if (!isAuthenticated) {
    return fallback || <LoadingState />;
  }

  // Check email verification if required
  if (requireVerifiedEmail && !user?.email_verified) {
    return <EmailVerificationRequired />;
  }

  // Authenticated - show content
  return <>{children}</>;
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange mx-auto mb-4" />
        <p className="text-cream/60">Loading...</p>
      </div>
    </div>
  );
}

function EmailVerificationRequired() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="max-w-md text-center">
        <h2 className="text-2xl font-bold text-cream mb-4">
          Email Verification Required
        </h2>
        <p className="text-cream/80 mb-6">
          Please verify your email address to access this content. Check your inbox for a verification email.
        </p>
      </div>
    </div>
  );
}
